import { useEffect, useRef } from 'react';

export type UseWebOtpArgs = {
  // Turn the listener off (e.g. once verification succeeded).
  enabled?: boolean;
  // Called with the digits read from the incoming SMS. Fill + submit from here.
  onReceive: (code: string) => void;
};

/**
 * WebOTP API listener. On supporting browsers (Chromium on Android) this reads
 * the one-time code from an incoming SMS whose last line is `@<host> #<code>`,
 * so the code can be filled and submitted without the user typing it.
 *
 * SMS-only, and a no-op where unsupported (iOS Safari, Firefox, desktop) — on
 * iOS the input's autocomplete="one-time-code" handles the suggestion instead.
 * Aborts on unmount / when disabled. Not for email codes (no browser API reads
 * an inbox).
 */
export function useWebOtp({ enabled = true, onReceive }: UseWebOtpArgs) {
  // Keep the latest callback without re-arming the listener on every render.
  const onReceiveRef = useRef(onReceive);
  onReceiveRef.current = onReceive;

  useEffect(() => {
    if (!enabled || typeof window === 'undefined' || !('OTPCredential' in window) || !navigator.credentials) {
      return;
    }

    const abortController = new AbortController();

    navigator.credentials
      .get({
        otp: { transport: ['sms'] },
        signal: abortController.signal,
      } as unknown as CredentialRequestOptions)
      .then((credential) => {
        const code = (credential as unknown as { code?: string } | null)?.code;
        if (code) onReceiveRef.current(code);
      })
      .catch(() => {
        // Aborted (unmount / manual submit) or no SMS arrived within the window.
      });

    return () => abortController.abort();
  }, [enabled]);
}
